/**
 * @file SiteFileLink.tsx
 * @description Адрес служебного файла сайта (карта сайта, robots.txt) с
 * кнопками «Скопировать» и «Открыть ↗» (phase19.16 Р1 → Р3в; SB45). Кнопки
 * переносятся под адрес на 360, высота 48 px.
 */

import { Copy, ExternalLink } from "lucide-react";
import { Fragment, type ReactNode } from "react";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";

/** Места переноса после «/» и «.»: длинный адрес рвётся по частям, а не посреди слова. */
function withSlashBreaks(url: string): ReactNode[] {
  return url.split(/(?<=[/.])/u).map((part, index) => (
    <Fragment key={index}>
      {part}
      <wbr />
    </Fragment>
  ));
}

export function SiteFileLink({ url, label, note }: { url: string; label: string; note: string }) {
  const notify = useNotify();
  function copy() {
    void navigator.clipboard
      ?.writeText(url)
      .then(() => notify.success("Адрес скопирован"))
      .catch(() => notify.error("Не удалось скопировать — выделите адрес вручную."));
  }
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-on-surface">{label}</span>
      {/* Адрес переносится по «/», а не посреди слова (QA Р3в, 360). */}
      <span className="type-body break-words font-mono text-on-surface">{withSlashBreaks(url)}</span>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={copy}>
          <Copy aria-hidden />
          Скопировать
        </Button>
        <Button asChild variant="outline">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink aria-hidden />
            Открыть ↗
          </a>
        </Button>
      </div>
      <p className="type-caption text-on-surface-variant">{note}</p>
    </div>
  );
}
